import { BasicTable, Icon, RadioInput, Typography } from "@/components/atoms";
import { ClientPaymentMethod } from "@/redux/apis/clientPayment/types";
import { clsx } from "@/utils";
import { useBulkBooking } from "../BulkBookingContext";
import { PaymentMethodSummary } from "../BulkPaymentMethod";

/** Body rows shown at once. Enough to recognise the shape of a file without a scroll marathon. */
const PREVIEW_ROWS = 10;

/**
 * Step 2 — "Select Header Row". The chosen row renders as the table's header; every other row sits
 * in the body with a radio, so picking one promotes it. Most files (including our template) put
 * headers in row 1, which is pre-selected, so this step is usually just a confirmation.
 */
export const SelectHeaderStep = ({ paymentMethod }: { paymentMethod?: ClientPaymentMethod }) => {
  const { workbook, headerRowIndex, setHeaderRowIndex, client } = useBulkBooking();
  const rows = workbook?.rows ?? [];
  const header = rows[headerRowIndex] ?? [];
  // Widest row wins, so a short header row doesn't hide columns further right.
  const columnCount = Math.max(...rows.map((row) => row.length), 1);
  const columns = Array.from({ length: columnCount }, (_, i) => i);

  const bodyRows = rows.map((row, index) => ({ row, index })).filter(({ index }) => index !== headerRowIndex);

  return (
    <div className="flex flex-col gap-4">
      {/* Names the card, not just the rule. The card sets the processing fee — an international one
          is surcharged at 3.5% against a domestic 1.7% — so seeing it here is what makes the
          estimates on the review step explainable. Names the client under OBO too, where the card is
          theirs rather than the operator's. */}
      <div className="flex items-start gap-2 rounded-md border border-neutral-dark-gray bg-neutral-surface-gray px-4 py-2.5">
        {/* Rotated per the design — iconsax draws InfoCircle dot-up, and this wants it dot-down. */}
        <Icon name="InfoCircle" variant="Bold" size="md" className="shrink-0 rotate-180 text-neutral-dark-gray" />
        <div className="flex min-w-0 flex-col gap-1.5">
          <Typography variant="action" className="text-neutral-dark-gray">
            {client
              ? `Bookings will be charged to ${client.name}'s default payment method.`
              : "Bookings will be charged to your default payment method."}
          </Typography>
          {paymentMethod && <PaymentMethodSummary method={paymentMethod} />}
        </div>
      </div>

      {/* Sized to content so the 13 columns stay readable and the wrapper scrolls. */}
      <BasicTable className="max-h-[45vh] [&>table]:!w-max [&>table]:!table-auto">
        <BasicTable.Row>
          <BasicTable.HeaderCell className="sticky top-0 z-10 w-14">
            <RadioInput
              className="[&>label]:sr-only"
              label={`Row ${headerRowIndex + 1} is the header row`}
              name="header-row"
              checked
              readOnly
            />
          </BasicTable.HeaderCell>
          {columns.map((column) => (
            <BasicTable.HeaderCell key={column} className="sticky top-0 z-10 min-w-[180px]">
              {header[column] || <span className="text-neutral-silver-gray">Column {column + 1}</span>}
            </BasicTable.HeaderCell>
          ))}
        </BasicTable.Row>
        <>
          {bodyRows.slice(0, PREVIEW_ROWS).map(({ row, index }) => (
            <BasicTable.Row key={index} className="cursor-pointer hover:bg-neutral-surface-gray" onClick={() => setHeaderRowIndex(index)}>
              <BasicTable.Cell className="w-14">
                <RadioInput
                  className="[&>label]:sr-only"
                  label={`Use row ${index + 1} as the header row`}
                  name="header-row"
                  checked={false}
                  onChange={() => setHeaderRowIndex(index)}
                />
              </BasicTable.Cell>
              {columns.map((column) => (
                <BasicTable.Cell key={column} className={clsx("min-w-[180px] truncate", !row[column] && "text-neutral-silver-gray")}>
                  {row[column] || "-"}
                </BasicTable.Cell>
              ))}
            </BasicTable.Row>
          ))}
        </>
      </BasicTable>

      {bodyRows.length > PREVIEW_ROWS && (
        <Typography variant="small" className="text-neutral-dark-gray">
          Showing the first {PREVIEW_ROWS} of {bodyRows.length} rows.
        </Typography>
      )}
    </div>
  );
};
